import { useEffect, useState, useRef } from 'react';
import { io, Socket } from 'socket.io-client';

interface NotificationData {
  userId: string;
  userName: string;
  userPhone: string;
  message: string;
  avatarUrl?: string;
  messageType: string;
  messageId?: string;
  botDisabled?: boolean;
}

interface Notification {
  type: 'new_message' | 'connected';
  timestamp: string;
  data?: NotificationData;
}

interface UseNotificationsOptions {
  onNewMessage?: (notification: Notification) => void;
  onShowToast?: (data: NotificationData) => void;
  enabled?: boolean;
}

export function useNotifications(options: UseNotificationsOptions = {}) {
  const { onNewMessage, onShowToast, enabled = true } = options;
  const [isConnected, setIsConnected] = useState(false);
  const [lastNotification, setLastNotification] = useState<Notification | null>(null);
  const socketRef = useRef<Socket | null>(null);

  // Solicitar permissão para notificações do navegador automaticamente
  useEffect(() => {
    if (enabled && 'Notification' in window) {
      if (Notification.permission === 'default') {
        // Solicitar permissão automaticamente
        console.log('📢 Solicitando permissão de notificação automaticamente...');
        Notification.requestPermission().then(permission => {
          console.log('   Permissão concedida:', permission);
          
          if (permission === 'granted') {
            // Ativar áudio automaticamente também
            const audioEnabled = localStorage.getItem('audioNotificationsEnabled');
            if (!audioEnabled) {
              console.log('🔊 Tentando ativar áudio automaticamente...');
              const audio = new Audio('/notification.mp3');
              audio.volume = 0.01;
              audio.play()
                .then(() => {
                  audio.pause();
                  localStorage.setItem('audioNotificationsEnabled', 'true');
                  console.log('✅ Áudio ativado automaticamente!');
                })
                .catch(err => {
                  console.log('⚠️ Não foi possível ativar áudio automaticamente:', err.message);
                  console.log('💡 Clique no botão "Ativar Som" para habilitar');
                });
            }
          }
        });
      } else if (Notification.permission === 'granted') {
        console.log('✅ Permissão de notificação já concedida');
      } else {
        console.log('❌ Permissão de notificação negada');
      }
    }
  }, [enabled]);

  // Conectar ao WebSocket (Socket.IO)
  useEffect(() => {
    if (!enabled) return;

    const connectToSocket = () => {
      try {
        // Usar a mesma URL do site (HTTPS) - o servidor Socket.IO está na mesma porta
        const baseUrl = window.location.origin;
        console.log('🔌 Tentando conectar ao WebSocket em:', baseUrl);
        
        // Criar conexão Socket.IO
        const socket = io(baseUrl, {
          path: '/socket.io',
          transports: ['polling', 'websocket'],
          reconnection: true,
          reconnectionDelay: 1000,
          reconnectionAttempts: 10,
          timeout: 10000,
          forceNew: true
        });

        socket.on('connect', () => {
          console.log('✅ Conectado ao WebSocket');
          console.log('   Socket ID:', socket.id);
          
          // Autenticar socket com seller_id
          try {
            const userStr = localStorage.getItem('user');
            if (userStr) {
              const user = JSON.parse(userStr);
              socket.emit('authenticate', {
                userId: user.id,
                sellerId: user.id // seller_id é o próprio ID do usuário logado
              });
              console.log('🔑 Socket autenticado com seller_id:', user.id);
            }
          } catch (error) {
            console.error('❌ Erro ao autenticar socket:', error);
          }
          
          setIsConnected(true);
        });
        
        socket.on('authenticated', (data) => {
          if (data.success) {
            console.log('✅ Socket autenticado com sucesso');
          }
        });

        socket.on('notification', (notification: Notification) => {
          try {
            // Só loga se for mensagem real (não conexão)
            if (notification.type !== 'connected') {
              console.log('📬 Notificação recebida via WebSocket:', notification);
            }

            setLastNotification(notification);

            // Se for uma nova mensagem, mostrar notificação do navegador e toast
            if (notification.type === 'new_message' && notification.data) {
              // Tocar som SEMPRE
              playNotificationSound();
              
              // Mostrar notificação do navegador
              showBrowserNotification(notification.data);
              
              // Mostrar toast visual
              if (onShowToast) {
                onShowToast(notification.data);
              }
              
              // Chamar callback se fornecido
              if (onNewMessage) {
                onNewMessage(notification);
              }
            }
          } catch (error) {
            console.error('❌ Erro ao processar notificação:', error);
          }
        });

        socket.on('disconnect', () => {
          console.log('❌ Desconectado do WebSocket');
          setIsConnected(false);
        });

        socket.on('connect_error', (error) => {
          console.error('❌ Erro de conexão WebSocket:', error.message);
          console.error('   Detalhes:', error);
          setIsConnected(false);
        });

        socket.on('reconnect_attempt', (attemptNumber) => {
          console.log(`🔄 Tentativa de reconexão ${attemptNumber}...`);
        });

        socket.on('reconnect_failed', () => {
          console.error('❌ Falha ao reconectar após múltiplas tentativas');
        });

        socketRef.current = socket;
      } catch (error) {
        console.error('❌ Erro ao conectar ao WebSocket:', error);
      }
    };

    connectToSocket();

    // Cleanup ao desmontar
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [enabled]); // Removido onNewMessage e onShowToast para evitar reconexões desnecessárias

  // Função para mostrar notificação do navegador
  const showBrowserNotification = (data: NotificationData) => {
    if ('Notification' in window && Notification.permission === 'granted') {
      const messagePreview = data.message.length > 100 
        ? data.message.substring(0, 100) + '...' 
        : data.message;

      const notification = new Notification(`Nova mensagem de ${data.userName}`, {
        body: messagePreview,
        icon: '/icon.png', // Adicione um ícone se tiver
        badge: '/icon.png',
        tag: `message-${data.userId}`, // Evita duplicatas
        requireInteraction: false,
        silent: false,
      });

      // Ao clicar na notificação, focar na janela e navegar para o usuário
      notification.onclick = () => {
        window.focus();
        window.location.href = `/usuarios/${data.userId}`;
        notification.close();
      };

      // Fechar automaticamente após 10 segundos
      setTimeout(() => notification.close(), 10000);

      // Tocar som de notificação (opcional)
      playNotificationSound();
    }
  };

  // Função para tocar som de notificação
  const playNotificationSound = () => {
    // Verificar se o usuário habilitou o áudio
    const audioEnabled = localStorage.getItem('audioNotificationsEnabled') === 'true';
    
    if (!audioEnabled) {
      console.log('🔇 Som desabilitado. Clique em "Ativar Notificações" para habilitar.');
      return;
    }

    try {
      const audio = new Audio('/notification.mp3');
      audio.volume = 0.7;
      audio.play().catch(err => {
        console.log('⚠️ Não foi possível tocar o som:', err);
        console.log('💡 Dica: Clique em "Ativar Notificações" no topo da página');
      });
    } catch (error) {
      console.error('❌ Erro ao tocar som:', error);
    }
  };

  return {
    isConnected,
    lastNotification,
  };
}
